/**
 * iter136.32 — Shared error-message extractor.
 *
 * The backend's FastAPI HTTPException ``detail`` field comes through
 * axios as ``error.response.data.detail`` and can be one of:
 *
 *   1. A plain string         — most common (e.g. ``"Save failed"``).
 *   2. A structured object    — used by gates that need a CTA, e.g.
 *      ``{ error, message, next_step_url, ... }``.
 *   3. A list of FastAPI 422 validation errors, each
 *      ``{ loc, msg, type, ... }``.
 *
 * Calling ``window.alert(detail)`` or ``toast.error(detail)`` directly
 * turned cases (2) and (3) into the useless ``"[object Object]"`` /
 * ``"[object Object],[object Object]"``. ``extractErr`` always returns
 * a usable string in ``.message`` and preserves any ``next_step_url``
 * + machine-readable ``code`` from structured responses so the caller
 * can render a CTA.
 *
 * iter136.33 — Every call also fire-and-forgets a POST to the
 * ``/api/operator/client-error`` log so super admins get a real-time
 * feed of which flows are failing in production. We skip:
 *   * 0/aborted/network errors with no response (offline noise),
 *   * 422 validation errors (user input, not a bug),
 *   * 401/403 (expected auth gating, not a bug).
 *
 * Usage
 * -----
 *   import { extractErr } from "@/lib/errors";
 *   import { toast } from "sonner";
 *
 *   try { ... }
 *   catch (e) {
 *     const { message, nextStep, code } = extractErr(e, "Save failed");
 *     toast.error(message);
 *   }
 */

const REPORT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/operator/client-error`;
// Skip-list: don't ship logs for known-benign HTTP statuses.
const SKIP_STATUSES = new Set([0, 401, 403, 422]);
const _RECENTLY_LOGGED = new Set();

function _maybeReport(e, fallback, parsed) {
  try {
    const status = e?.response?.status;
    // No response object → axios timeout / network error / aborted.
    if (status == null) return;
    if (SKIP_STATUSES.has(Number(status))) return;

    // Cheap dedupe so the same broken polling loop doesn't spam the
    // log; key = status + URL + first 80 chars of message.
    const url = e?.config?.url || "";
    const dedupe = `${status}|${url}|${(parsed.message || "").slice(0, 80)}`;
    if (_RECENTLY_LOGGED.has(dedupe)) return;
    _RECENTLY_LOGGED.add(dedupe);
    setTimeout(() => _RECENTLY_LOGGED.delete(dedupe), 60_000);

    const body = JSON.stringify({
      message: parsed.message || fallback || "unknown",
      fallback: fallback || null,
      code: parsed.code || null,
      status: Number(status),
      method: (e?.config?.method || "").toUpperCase() || null,
      url: url || null,
      page: typeof window !== "undefined" ? window.location?.pathname : null,
      user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 400) : null,
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // Fire-and-forget; doesn't block the page even on unload.
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(REPORT_URL, blob);
      return;
    }
    fetch(REPORT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => { /* logging is best-effort */ });
  } catch { /* never let the logger crash the caller */ }
}

export function extractErr(e, fallback = "Something went wrong") {
  const detail = e?.response?.data?.detail;
  let parsed;

  if (typeof detail === "string" && detail.trim()) {
    parsed = { message: detail.trim(), nextStep: null, code: null };
  } else if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    parsed = {
      message: detail.message || detail.error || fallback,
      nextStep: detail.next_step_url || null,
      code: detail.error || detail.code || null,
    };
  } else if (Array.isArray(detail) && detail.length) {
    parsed = {
      message: detail
        .map((d) => d?.msg || d?.message || JSON.stringify(d))
        .join("; "),
      nextStep: null,
      code: null,
    };
  } else if (typeof e?.message === "string" && e.message.trim()) {
    parsed = { message: e.message.trim(), nextStep: null, code: null };
  } else {
    parsed = { message: fallback, nextStep: null, code: null };
  }

  _maybeReport(e, fallback, parsed);
  return parsed;
}
